/**
 * FluentMentor marketing landing — fluentmentor.com.
 *
 * The mobile app at apps/mobile is the actual product. This page exists to:
 *   - Tell the world what FluentMentor is.
 *   - Drop visitors into the App Store / Play Store via universal links once
 *     the mobile app ships.
 *   - Host the privacy + terms URLs that App Store review requires.
 *
 * Deliberately static — no SPA routing, no API calls, no auth. Single-file
 * component so Cloudflare Pages serves a tiny build.
 */
import * as React from 'react';

const COLORS = {
  paper: '#FAF6F0',
  paper2: '#F3ECE0',
  ink: '#1A1614',
  ink2: '#3A322D',
  ink3: '#6B5F57',
  coral: '#F0563A',
  coralSoft: '#FFE9E2',
  lime: '#9DD86F',
  hair: '#E3DCCF',
} as const;

const FONT_DISPLAY = '"Fraunces", Georgia, "Times New Roman", serif';
const FONT_SANS =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace';

// Universal link to App Store. Once the build ships, the numeric id 6764095464
// resolves and the deep-link works automatically.
const APP_STORE_URL =
  'https://apps.apple.com/app/fluentmentor/id6764095464';
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.fluentmentor.app';

export default function App(): React.ReactElement {
  return (
    <div style={styles.root}>
      <Hero />
      <Features />
      <HowItWorks />
      <Cta />
      <Footer />
    </div>
  );
}

function Hero(): React.ReactElement {
  return (
    <header style={styles.hero}>
      <div style={styles.heroBg} aria-hidden />
      <nav style={styles.nav}>
        <Mark dark />
        <div style={styles.navLinks}>
          <a href="#features" style={styles.navLink}>
            Features
          </a>
          <a href="#how" style={styles.navLink}>
            How it works
          </a>
          <a href="/privacy.html" style={styles.navLink}>
            Privacy
          </a>
        </div>
      </nav>
      <div style={styles.heroBody}>
        <span style={styles.eyebrow}>TR ↔ EN · ASSESSMENT-DRIVEN LEARNING</span>
        <h1 style={styles.heroTitle}>
          Say it <span style={styles.heroItalic}>right.</span>
        </h1>
        <p style={styles.heroSub}>
          Translate the way a native would. Practice pronunciation with
          phoneme-level feedback. Lock in vocabulary with personalised
          flashcards pulled straight from your translations.
        </p>
        <div style={styles.heroCtas}>
          <StoreBadge href={APP_STORE_URL} kind="apple" />
          <StoreBadge href={PLAY_STORE_URL} kind="google" />
        </div>
        <p style={styles.comingSoon}>
          iOS first, Android shortly after. v1.0 ships from Apr 2026.
        </p>
      </div>
    </header>
  );
}

function Features(): React.ReactElement {
  return (
    <section id="features" style={styles.section}>
      <SectionHead eyebrow="What's inside" title="Three loops, one app" />
      <div style={styles.grid3}>
        <Feature
          tag="01"
          title="Translate with context"
          body="Tone, audience, and culture all shape the output. Pick the variant that fits, or compare side-by-side. Powered by GPT-4 + Gemini in parallel."
          accent={COLORS.coral}
        />
        <Feature
          tag="02"
          title="Pronunciation coach"
          body="Record yourself, get phoneme-level scoring backed by Microsoft Azure Speech. Find the syllables you missed and drill them."
          accent={COLORS.lime}
        />
        <Feature
          tag="03"
          title="Personalised lessons"
          body="Every translation feeds your flashcard deck. Daily streak, weekly XP report, and a 7-tier league against other learners."
          accent="#6E5AD9"
        />
      </div>
    </section>
  );
}

function HowItWorks(): React.ReactElement {
  return (
    <section id="how" style={{ ...styles.section, background: COLORS.paper2 }}>
      <SectionHead eyebrow="Daily loop" title="Ten minutes is enough" />
      <ol style={styles.stepList}>
        <Step
          num="1"
          title="Translate something you actually wrote"
          body="A text you're sending, an email you're drafting, a tweet — drop it in. The translator gives you 3 variants tuned to tone + audience."
        />
        <Step
          num="2"
          title="Save the new vocabulary"
          body="The words you didn't know auto-add to your deck. We schedule reviews (SRS) so you actually retain them."
        />
        <Step
          num="3"
          title="Drill pronunciation"
          body="When you hit a tough word, the coach grades each phoneme and shows where your tongue went wrong. ~30 seconds per word."
        />
        <Step
          num="4"
          title="Stay on the streak"
          body="Daily reminder, weekly XP report, league with 30 other learners. Top 5 promote. Designed so 10 minutes a day actually moves the needle."
        />
      </ol>
    </section>
  );
}

function Cta(): React.ReactElement {
  return (
    <section style={{ ...styles.section, paddingBottom: 120 }}>
      <div style={styles.ctaCard}>
        <h2 style={styles.ctaTitle}>Ship something useful today.</h2>
        <p style={styles.ctaSub}>
          The next 30 days of your language learning are right here. Free to
          start, premium when you need the harder ladders.
        </p>
        <div style={{ ...styles.heroCtas, justifyContent: 'center' }}>
          <StoreBadge href={APP_STORE_URL} kind="apple" />
          <StoreBadge href={PLAY_STORE_URL} kind="google" />
        </div>
      </div>
    </section>
  );
}

function Footer(): React.ReactElement {
  return (
    <footer style={styles.footer}>
      <div style={styles.footerRow}>
        <Mark dark />
        <div style={styles.footerLinks}>
          <a href="/privacy.html" style={styles.footerLink}>
            Privacy
          </a>
          <a href="/terms.html" style={styles.footerLink}>
            Terms
          </a>
          <a href="mailto:hello@fluentmentor.com" style={styles.footerLink}>
            Contact
          </a>
        </div>
      </div>
      <p style={styles.copyright}>
        © 2026 FluentMentor. Made with care in İstanbul.
      </p>
    </footer>
  );
}

// ─── Building blocks ─────────────────────────────────────────────────────

function Mark({ dark = false }: { dark?: boolean }): React.ReactElement {
  return (
    <div style={styles.markRow}>
      <div style={styles.markBox}>
        <span style={styles.markGlyph}>Fm</span>
      </div>
      <span
        style={{
          ...styles.markText,
          color: dark ? COLORS.paper : COLORS.ink,
        }}
      >
        FluentMentor
      </span>
    </div>
  );
}

function SectionHead({
  eyebrow,
  title,
}: {
  eyebrow: string;
  title: string;
}): React.ReactElement {
  return (
    <div style={styles.sectionHead}>
      <span style={{ ...styles.eyebrow, color: COLORS.coral }}>{eyebrow}</span>
      <h2 style={styles.sectionTitle}>{title}</h2>
    </div>
  );
}

function Feature({
  tag,
  title,
  body,
  accent,
}: {
  tag: string;
  title: string;
  body: string;
  accent: string;
}): React.ReactElement {
  return (
    <article style={styles.featureCard}>
      <span style={{ ...styles.featureTag, color: accent }}>{tag}</span>
      <h3 style={styles.featureTitle}>{title}</h3>
      <p style={styles.featureBody}>{body}</p>
      <span style={{ ...styles.featureRule, background: accent }} aria-hidden />
    </article>
  );
}

function Step({
  num,
  title,
  body,
}: {
  num: string;
  title: string;
  body: string;
}): React.ReactElement {
  return (
    <li style={styles.step}>
      <div style={styles.stepNum}>{num}</div>
      <div>
        <h3 style={styles.stepTitle}>{title}</h3>
        <p style={styles.stepBody}>{body}</p>
      </div>
    </li>
  );
}

function StoreBadge({
  href,
  kind,
}: {
  href: string;
  kind: 'apple' | 'google';
}): React.ReactElement {
  const label = kind === 'apple' ? 'App Store' : 'Google Play';
  const sub = kind === 'apple' ? 'Download on the' : 'Get it on';
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      style={styles.storeBadge}
      aria-label={`${sub} ${label}`}
    >
      <span style={styles.storeBadgeSub}>{sub}</span>
      <span style={styles.storeBadgeLabel}>{label}</span>
    </a>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: COLORS.paper,
    color: COLORS.ink,
    fontFamily: FONT_SANS,
    overflow: 'hidden',
  },
  hero: {
    position: 'relative',
    paddingTop: 28,
    paddingBottom: 100,
    paddingLeft: 'clamp(20px, 6vw, 96px)',
    paddingRight: 'clamp(20px, 6vw, 96px)',
    background: COLORS.ink,
    color: COLORS.paper,
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    top: -160,
    right: -180,
    width: 480,
    height: 480,
    borderRadius: 240,
    border: `60px solid ${COLORS.coral}`,
    opacity: 0.32,
    pointerEvents: 'none',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 1,
  },
  navLinks: {
    display: 'flex',
    gap: 28,
    alignItems: 'center',
  },
  navLink: {
    fontFamily: FONT_SANS,
    fontSize: 14,
    color: 'rgba(250, 246, 240, 0.78)',
    textDecoration: 'none',
  },
  heroBody: {
    position: 'relative',
    maxWidth: 720,
    paddingTop: 80,
  },
  eyebrow: {
    display: 'inline-block',
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 2,
    color: COLORS.lime,
    marginBottom: 24,
  },
  heroTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: 'clamp(56px, 9vw, 112px)',
    fontWeight: 400,
    lineHeight: 0.95,
    letterSpacing: -3,
    margin: 0,
    color: COLORS.paper,
  },
  heroItalic: {
    fontStyle: 'italic',
    color: COLORS.coral,
  },
  heroSub: {
    marginTop: 28,
    fontSize: 'clamp(16px, 1.4vw, 19px)',
    lineHeight: 1.55,
    color: 'rgba(250, 246, 240, 0.78)',
    maxWidth: 560,
  },
  heroCtas: {
    marginTop: 36,
    display: 'flex',
    gap: 14,
    flexWrap: 'wrap',
  },
  comingSoon: {
    marginTop: 18,
    fontFamily: FONT_MONO,
    fontSize: 12,
    color: 'rgba(250, 246, 240, 0.55)',
    letterSpacing: 0.6,
  },

  section: {
    paddingTop: 96,
    paddingBottom: 96,
    paddingLeft: 'clamp(20px, 6vw, 96px)',
    paddingRight: 'clamp(20px, 6vw, 96px)',
  },
  sectionHead: {
    maxWidth: 720,
    marginBottom: 56,
  },
  sectionTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: 'clamp(36px, 5.5vw, 60px)',
    fontWeight: 400,
    letterSpacing: -1.4,
    lineHeight: 1.05,
    margin: '8px 0 0',
    color: COLORS.ink,
  },

  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 24,
  },
  featureCard: {
    position: 'relative',
    padding: 32,
    background: COLORS.paper,
    borderRadius: 28,
    border: `1px solid ${COLORS.hair}`,
    overflow: 'hidden',
  },
  featureTag: {
    fontFamily: FONT_MONO,
    fontSize: 11,
    letterSpacing: 1.6,
    fontWeight: 600,
  },
  featureTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: 26,
    fontWeight: 500,
    letterSpacing: -0.6,
    margin: '12px 0 14px',
    color: COLORS.ink,
  },
  featureBody: {
    fontSize: 15,
    lineHeight: 1.6,
    color: COLORS.ink2,
    margin: 0,
  },
  featureRule: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
  },

  stepList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    maxWidth: 760,
  },
  step: {
    display: 'grid',
    gridTemplateColumns: '64px 1fr',
    gap: 24,
    alignItems: 'flex-start',
  },
  stepNum: {
    width: 56,
    height: 56,
    borderRadius: 28,
    background: COLORS.coral,
    color: COLORS.paper,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT_DISPLAY,
    fontSize: 22,
    fontWeight: 500,
  },
  stepTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: 22,
    fontWeight: 500,
    letterSpacing: -0.4,
    margin: 0,
    color: COLORS.ink,
  },
  stepBody: {
    fontSize: 15,
    lineHeight: 1.6,
    color: COLORS.ink2,
    margin: '8px 0 0',
  },

  ctaCard: {
    background: COLORS.coral,
    color: COLORS.paper,
    borderRadius: 36,
    padding: 'clamp(40px, 6vw, 80px)',
    textAlign: 'center',
    maxWidth: 880,
    margin: '0 auto',
  },
  ctaTitle: {
    fontFamily: FONT_DISPLAY,
    fontSize: 'clamp(32px, 5vw, 52px)',
    fontWeight: 500,
    letterSpacing: -1,
    lineHeight: 1.1,
    margin: 0,
    color: COLORS.paper,
  },
  ctaSub: {
    marginTop: 18,
    fontSize: 17,
    lineHeight: 1.5,
    color: 'rgba(250, 246, 240, 0.85)',
    maxWidth: 520,
    marginLeft: 'auto',
    marginRight: 'auto',
  },

  footer: {
    background: COLORS.ink,
    color: COLORS.paper,
    paddingTop: 56,
    paddingBottom: 32,
    paddingLeft: 'clamp(20px, 6vw, 96px)',
    paddingRight: 'clamp(20px, 6vw, 96px)',
  },
  footerRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 28,
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 32,
    borderBottom: '1px solid rgba(250, 246, 240, 0.12)',
  },
  footerLinks: {
    display: 'flex',
    gap: 28,
    alignItems: 'center',
  },
  footerLink: {
    fontSize: 14,
    color: 'rgba(250, 246, 240, 0.78)',
    textDecoration: 'none',
  },
  copyright: {
    fontFamily: FONT_MONO,
    fontSize: 11,
    color: 'rgba(250, 246, 240, 0.45)',
    letterSpacing: 0.6,
    marginTop: 24,
  },

  markRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  markBox: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: COLORS.coral,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.paper,
  },
  markGlyph: {
    fontFamily: FONT_DISPLAY,
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: -0.4,
  },
  markText: {
    fontFamily: FONT_DISPLAY,
    fontSize: 17,
    fontWeight: 500,
    letterSpacing: -0.3,
  },

  storeBadge: {
    display: 'inline-flex',
    flexDirection: 'column',
    padding: '10px 22px',
    background: COLORS.paper,
    color: COLORS.ink,
    textDecoration: 'none',
    borderRadius: 14,
    border: `1px solid ${COLORS.hair}`,
    minWidth: 168,
  },
  storeBadgeSub: {
    fontFamily: FONT_MONO,
    fontSize: 10,
    letterSpacing: 1.4,
    color: COLORS.ink3,
  },
  storeBadgeLabel: {
    fontFamily: FONT_DISPLAY,
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: -0.3,
    marginTop: 2,
    color: COLORS.ink,
  },
};
